import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { HelpChatApiService } from './help-chat-api.service';

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';

  /** What was typed, the answer's raw markdown, or for an error the message to show. */
  text: string;

  /**
   * The answer rendered from markdown (assistant answers only). Rendered once when the answer
   * arrives rather than in the template, so change detection never re-parses it.
   */
  html?: string;

  /** The question could not be answered; `text` is the message to show. */
  error?: boolean;

  /** A spoken question whose transcript has not come back yet (user messages only). */
  transcribing?: boolean;

  /**
   * ISO-639-1 code of the language the message is in, when known: the language a spoken question
   * was recognised in, carried over to its answer so the answer is read aloud in the same one.
   */
  languageCode?: string;

  /** Object URL of the answer's speech once fetched, so listening again costs nothing. Revoked on clear(). */
  audioUrl?: string;
}

/** Playback of one answer: fetching its speech, or playing it. */
export interface SpeechState {
  messageId: number;
  status: 'loading' | 'playing';
}

/** Messages shown in the conversation when something goes wrong. */
export const CHAT_ERRORS = {
  failed: 'The question could not be answered. Please try again later.',
  notUnderstood: 'No speech was recognised in the recording. Please try again.',
  speech: 'The answer could not be read aloud. Please try again later.',
  micDenied: 'The microphone is blocked. Allow it for this site in the browser to ask aloud.',
  micUnavailable: 'No microphone could be opened. Check that one is connected and not used by another app.',
  tooShort: 'The recording was too short. Hold on a moment longer before pressing stop.',
  noSound: 'The microphone recorded no sound. Check that it is not muted.',
  noSoundChooseMicrophone: 'The microphone recorded no sound. Check that it is not muted, or choose another one below.',
} as const;

/**
 * State of the AI help chat.
 *
 * A root service rather than component state: the chat tab is torn down whenever the panel switches
 * to the contents, and the conversation should still be there on the way back. It lasts as long as
 * the page; nothing is stored.
 *
 * The service is stateless, so every question is sent on its own; the history here is for the
 * reader, not for the model.
 *
 * Voice is opt-in behind `voiceEnabled$`. Even then speech is never fetched with an answer: a
 * text-to-speech call is made only when the user asks to listen to a particular answer, and its MP3
 * is then kept on the message.
 */
@Injectable({ providedIn: 'root' })
export class HelpChatStore {
  private readonly _api = inject(HelpChatApiService);
  private readonly _translate = inject(TranslateService);

  private readonly _enabled = new BehaviorSubject<boolean>(false);
  private readonly _messages = new BehaviorSubject<ChatMessage[]>([]);
  private readonly _pending = new BehaviorSubject<boolean>(false);
  private readonly _voiceEnabled = new BehaviorSubject<boolean>(false);
  private readonly _speech = new BehaviorSubject<SpeechState | null>(null);

  /** Whether the backend has a chat service configured. Off until it says so. */
  readonly enabled$ = this._enabled.asObservable();

  readonly messages$ = this._messages.asObservable();

  /** A question is out and its answer has not come back yet. */
  readonly pending$ = this._pending.asObservable();

  /** The voice controls are shown: the microphone, and "Listen" on every answer. */
  readonly voiceEnabled$ = this._voiceEnabled.asObservable();

  /** The answer being fetched as speech or being played, if any. One at a time. */
  readonly speech$ = this._speech.asObservable();

  private _lastId = 0;
  private _audio: HTMLAudioElement | null = null;
  private _statusKnown = false;
  private _statusLoading = false;

  /**
   * Asks the backend whether the chat is available (configured, and the AI service reachable). Only
   * "available" is kept for the rest of the session; "not available" and a failed request leave the
   * chat switched off for now, and the next call (the next time the help panel opens) asks again.
   *
   * `force` asks again even when an answer is known: used after a question has failed, so a service
   * that has gone away takes the Ask AI tab with it instead of failing every further question.
   */
  async loadStatus(force = false): Promise<void> {
    if ((this._statusKnown && !force) || this._statusLoading) {
      return;
    }

    this._statusLoading = true;

    try {
      const enabled = await firstValueFrom(this._api.status());
      this._enabled.next(enabled);

      // Only "available" is kept for the session. "Not available" is asked again the next time the
      // panel opens, so an AI service started after the page was loaded shows up without a reload.
      // The backend caches its own probe, so asking again is cheap.
      this._statusKnown = enabled;
    } catch {
      this._enabled.next(false);
    } finally {
      this._statusLoading = false;
    }
  }

  get pending(): boolean {
    return this._pending.value;
  }

  setVoiceEnabled(enabled: boolean): void {
    this._voiceEnabled.next(enabled);

    // Switching the voice off should not leave an answer talking.
    if (!enabled) {
      this.stopSpeaking();
    }
  }

  async ask(question: string): Promise<void> {
    const text = question.trim();

    if (!text || this._pending.value) {
      return;
    }

    this._push({ role: 'user', text });
    this._pending.next(true);

    try {
      const answer = await firstValueFrom(this._api.ask(text));

      if (!answer.trim()) {
        throw new Error('empty answer');
      }

      await this._pushAnswer(answer);
    } catch {
      this._push({ role: 'assistant', text: CHAT_ERRORS.failed, error: true });
      void this.loadStatus(true);
    } finally {
      this._pending.next(false);
    }
  }

  /**
   * Asks a recorded question. The recording is transcribed by the service, so the question bubble
   * shows a placeholder until the transcript comes back with the answer. A recording nothing could
   * be made of is reported where the question would have been.
   */
  async askVoice(recording: Blob): Promise<void> {
    if (!recording.size || this._pending.value) {
      return;
    }

    const questionId = this._push({ role: 'user', text: '', transcribing: true });
    this._pending.next(true);

    try {
      // No language hint: the speech service detects the language itself, and the UI language is
      // a poor guess at what someone actually speaks.
      const response = await firstValueFrom(this._api.askVoice(recording));

      if (!response.question) {
        throw new HttpErrorResponse({ status: 400 });
      }

      const languageCode = response.languageCode ?? undefined;

      this._patch(questionId, { text: response.question, transcribing: false, languageCode });

      if (!response.answer.trim()) {
        this._push({ role: 'assistant', text: CHAT_ERRORS.failed, error: true });
        return;
      }

      await this._pushAnswer(response.answer, languageCode);
    } catch (error) {
      this._remove(questionId);

      // 400 is the service's word for "no speech in the recording"; anything else is the service
      // failing, as for a typed question.
      const notUnderstood = error instanceof HttpErrorResponse && error.status === 400;
      this._push({
        role: 'assistant',
        text: notUnderstood ? CHAT_ERRORS.notUnderstood : CHAT_ERRORS.failed,
        error: true,
      });

      if (!notUnderstood) {
        void this.loadStatus(true);
      }
    } finally {
      this._pending.next(false);
    }
  }

  /**
   * Reads an answer aloud, or stops it if it is the one playing. The MP3 is fetched on the first
   * request only and kept on the message afterwards.
   */
  async speak(messageId: number): Promise<void> {
    if (this._speech.value?.messageId === messageId) {
      this.stopSpeaking();
      return;
    }

    this.stopSpeaking();

    const message = this._messages.value.find((item) => item.id === messageId);

    if (!message || message.role !== 'assistant' || message.error || !message.text.trim()) {
      return;
    }

    this._speech.next({ messageId, status: 'loading' });

    try {
      const url = message.audioUrl ?? (await this._fetchSpeech(message));

      // The user may have stopped or started another answer while this one was being fetched; its
      // audio is still kept for later.
      this._patch(messageId, { audioUrl: url });

      if (this._speech.value?.messageId !== messageId) {
        return;
      }

      await this._play(messageId, url);
    } catch {
      // A request the user has already moved on from has nothing to report.
      if (this._speech.value?.messageId !== messageId) {
        return;
      }

      this._speech.next(null);
      this._push({ role: 'assistant', text: CHAT_ERRORS.speech, error: true });
    }
  }

  /**
   * Shows a voice problem that happened before any question was sent (a refused microphone, say)
   * in the conversation, where the answer to it would have appeared.
   */
  reportVoiceError(message: string): void {
    this._push({ role: 'assistant', text: message, error: true });
  }

  stopSpeaking(): void {
    if (this._audio) {
      this._audio.pause();
      this._audio.onended = null;
      this._audio.onerror = null;
      this._audio = null;
    }

    if (this._speech.value) {
      this._speech.next(null);
    }
  }

  clear(): void {
    this.stopSpeaking();

    for (const message of this._messages.value) {
      if (message.audioUrl) {
        URL.revokeObjectURL(message.audioUrl);
      }
    }

    this._messages.next([]);
  }

  private async _pushAnswer(answer: string, languageCode?: string): Promise<void> {
    // Loaded on the first answer, not with the panel: the panel is on every screen, and the
    // markdown renderer has no business in the initial bundle.
    const { renderHelpMarkdown } = await import('../help-article-page/help-markdown');

    this._push({ role: 'assistant', text: answer, html: renderHelpMarkdown(answer), languageCode });
  }

  private async _fetchSpeech(message: ChatMessage): Promise<string> {
    const mp3 = await firstValueFrom(
      // The language the question was asked in, else the UI's: the voice needs to know how to
      // pronounce what it reads.
      this._api.speak(message.text, message.languageCode ?? this._translate.currentLang ?? undefined)
    );

    return URL.createObjectURL(mp3);
  }

  private _play(messageId: number, url: string): Promise<void> {
    const audio = new Audio(url);
    this._audio = audio;

    const finished = () => {
      if (this._audio === audio) {
        this._audio = null;
        this._speech.next(null);
      }
    };

    audio.onended = finished;
    // A decode failure after play() resolved would otherwise leave the answer marked as playing.
    audio.onerror = finished;

    this._speech.next({ messageId, status: 'playing' });

    return audio.play();
  }

  private _push(message: Omit<ChatMessage, 'id'>): number {
    const id = ++this._lastId;
    this._messages.next([...this._messages.value, { ...message, id }]);

    return id;
  }

  private _patch(id: number, changes: Partial<ChatMessage>): void {
    this._messages.next(this._messages.value.map((item) => (item.id === id ? { ...item, ...changes } : item)));
  }

  private _remove(id: number): void {
    this._messages.next(this._messages.value.filter((item) => item.id !== id));
  }
}
